/* eslint-disable max-lines-per-function */
import ParameterValidator from '@mvps-genesis/parameter-validator';
import EventBus from '@mvps-genesis/event-bus';
import {MessageContainer, Message} from 'extensions/messages';

/*
 *--------------------------------------------------------------------------
 * AjaxAddToCart
 *--------------------------------------------------------------------------
 *
 * Asynchronously handle the form submission of multiple Miva ADPR forms.
 *
 */
export default class AjaxMultiAddToCart {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * The list of form elements
	 *
	 * @var	{HTMLElement[]}
	 */
	formElements;

	/**
	 * The submit button element
	 *
	 * @var	{HTMLElement}
	 */
	submitButtonElement;

	/**
	 * Text to apply to the submit button element when the form submission is processing
	 *
	 * @var	{String}
	 */
	processingButtonText;

	/**
	 * The success message object
	 *
	 * @var	{Object}
	 */
	successMessage;

	/**
	 * The error message object
	 *
	 * @var	{Object}
	 */
	errorMessage;

	/**
	 * Selector for identifying error messages
	 *
	 * @var	{String}
	 */
	errorMessageSelector;

	/**
	 * The "other" message object
	 *
	 * @var	{Object}
	 */
	otherMessage;

	/**
	 * MessageContainer class object
	 *
	 * @var {MessageContainer} extensions/messages
	 */
	messageContainer;

	/**
	 * Property to update the text value for the submit button element
	 *
	 * @var {String}
	 */
	submitButtonTextProperty;

	/**
	 * The submit button element text value
	 *
	 * @var {String}
	 */
	submitButtonText;

	/**
	 * Scroll to message object
	 *
	 * @var {Object}
	 */
	scrollToMessage;

	/**
	 * List of add to cart success messages
	 *
	 * @var {Array}
	 */
	successMessages;

	/**
	 * List of multi add to cart error messages
	 *
	 * @var {Array}
	 */
	errorMessages;

	/**
	 * Create a new AjaxMultiAddToCart object.
	 *
	 * @param  {Array}               formElements                       The list of form elements.
	 * @param  {Object}              options.errorMessage               The error message object.
	 * @param  {String}              options.errorMessageSelector       Selector for stock message errors. Defaults to '.g-messages--error'.
	 * @param  {Object}              options.otherMessage               The "other" message object.
	 * @param  {String}              options.processingButtonText       Text to apply to the submit button element when the form submission is processing. Defaults to 'Processing...'.
	 * @param  {HTMLElement|String}  options.responseMessageElement     The message element to target for response messages. Defaults to '.js-add-to-cart-message'.
	 * @param  {Boolean|String}      options.scrollToMessage            Scroll to message mode setting, which controls scroll to message container behavior after add to cart action. Supports the following values: true = always scroll to message container; false = never scroll to message container; 'success' = only scroll to message container on successful add to cart; 'error' = only scroll to message container on unsuccessful add to cart. Defaults to false.
	 * @param  {Object}              options.scrollToMessageOptions     Scroll to message options object, supports the Element.scrollIntoView() options listed here - https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView. Defaults to {behavior: 'smooth', block: 'center'}.
	 * @param  {HTMLElement|String}  options.submitButtonElement        The submit button element. Defaults to '.js-add-to-cart'.
	 * @param  {Object}              options.successMessage             The success message object.
	 */
	constructor (
		formElements,
		{
			errorMessage = {
				message: 'Item(s) could not be added to cart.',
				options: {
					autohide: false,
					type: 'warning'
				}
			},
			errorMessageSelector = '.g-messages--error',
			otherMessage = {
				message: 'Please review your selection.',
				options: {
					autohide: true,
					type: 'warning'
				}
			},
			processingButtonText = 'Processing...',
			responseMessageElement = '#js-add-to-cart-message',
			scrollToMessage = false,
			scrollToMessageOptions = {
				behavior: 'smooth',
				block: 'center'
			},
			submitButtonElement = '.js-add-to-cart',
			successMessage = {
				message: 'Item(s) added to cart.',
				options: {
					autohide: true,
					type: 'success'
				}
			}
		} = {}
	) {
		this.#validator = new ParameterValidator(this);

		this.formElements = this.#validator.isArray('formElements', formElements);

		this.messageContainer = new MessageContainer({container: this.#validator.isElement('options.responseMessageElement', responseMessageElement)});
		this.errorMessage = this.#validator.isObject('options.errorMessage', errorMessage);
		this.errorMessageSelector = this.#validator.isString('options.errorMessageSelector', errorMessageSelector);
		this.otherMessage = this.#validator.isObject('options.otherMessage', otherMessage);
		this.successMessage = this.#validator.isObject('options.successMessage', successMessage);
		this.successMessages = [];
		this.errorMessages = [];

		this.submitButtonElement = this.#validator.isElement('options.submitButtonElement', submitButtonElement, {container: this.formElement});
		this.submitButtonTextProperty = this.submitButtonElement instanceof HTMLButtonElement ? 'textContent' : 'value';
		this.submitButtonText = this.submitButtonElement[this.submitButtonTextProperty];
		this.processingButtonText = this.#validator.isString('options.processingButtonText', processingButtonText);

		this.scrollToMessage = {
			mode: this.#validator.inSet('options.scrollToMessage', scrollToMessage, [
				true,
				false,
				'success',
				'error'
			]),
			scrollIntoView: this.#validator.isObject('options.scrollToMessageOptions', scrollToMessageOptions)
		};

		this.bindEvents();
	}

	/**
	 * Bind form element events.
	 */
	bindEvents () {
		this.submitButtonElement.addEventListener('click', this.onSubmit.bind(this), false);
	}

	/**
	 * Handle on submit events.
	 *
	 * @param  {Event} event The submit event.
	 * @return {void}
	 */
	async onSubmit (event) {
		event.preventDefault();
		event.stopImmediatePropagation();

		// Clear previous response messages
		this.messageContainer.clear();

		let payload = {
			data: null,
			iterations: [],
			messages: {
				errors: [],
				success: []
			},
			success: false
		};

		const adpmFormData = new FormData();
		let adpmProductsIndex = 1;

		// Build request input parameters with form data
		this.formElements.forEach((formElement) => {
			const formData = new FormData(formElement);
			const quantity = parseInt(formData.get('Quantity'), 10);

			if (isNaN(quantity) || quantity < 1) {
				return;
			}

			payload.iterations.push({
				form: formElement,
				formData: formData
			});

			adpmFormData.append(`Products[${adpmProductsIndex}]:quantity`, quantity);
			adpmFormData.append(`Products[${adpmProductsIndex}]:code`, formData.get('Product_Code'));
			adpmFormData.append(`Products[${adpmProductsIndex}]:attributes[1]:code`, formData.get('Product_Attributes[1]:code'));
			adpmFormData.append(`Products[${adpmProductsIndex}]:attributes[1]:value`, formData.get('Product_Attributes[1]:value'));

			adpmProductsIndex++;
		});

		if (adpmFormData.entries().next().done === true) {
			this.messageContainer.add(new Message('Please select product(s) to add to cart your cart.', {
				...this.errorMessage.options,
				autohide: true
			}));

			return;
		}

		adpmFormData.append('Action', 'ADPM');

		const requestUrl = this.submitButtonElement?.dataset.action ?? `${window.mivaJS?.Basket_Url}ajax=1`;
		const request = new Request(requestUrl, {
			body: adpmFormData,
			method: 'POST'
		});

		payload.request = request;

		// Disable form submission while request is processing and set text of submit element to processing text
		this.submitButtonElement.disabled = true;
		this.submitButtonElement[this.submitButtonTextProperty] = this.processingButtonText;

		// Send the ADPM request
		const response = await fetch(request);

		if (response.ok) {
			const responseHtml = await response.text();
			const responseDom = new DOMParser().parseFromString(responseHtml, 'text/html');
			const pageCodeResponseHeader = response.headers.get('g-mm-page-code');

			payload.data = responseDom;

			this.submitButtonElement.disabled = false;
			this.submitButtonElement[this.submitButtonTextProperty] = this.submitButtonText;

			if (pageCodeResponseHeader === 'BASK') {
				payload.success = true;

				this.onSuccess();

				const errorMessage = this.getErrorMessage(payload.data);

				if (errorMessage === '') {
					this.messageContainer.add(new Message(this.successMessages.join('<br>'), this.successMessage.options));

					// Reset quantity inputs on successful submission
					this.formElements.forEach((formElement) => {
						const quantityElement = formElement.querySelector('input[name="Quantity"]');

						if (!quantityElement) {
							return;
						}

						quantityElement.value = 0;

						quantityElement.dispatchEvent(new Event('change', {bubbles: true}));
					});
				} else {
					this.errorMessages.push(errorMessage);

					const message = `The following issues occurred:<br><strong>${this.errorMessages.join('<br>')}</strong><br>Please review your cart.`;

					this.messageContainer.add(new Message(message, this.errorMessage.options));
				}
			} else {
				this.onOther();
			}

			payload.messages.errors = this.errorMessages;
			payload.messages.success = this.successMessages;

			this.handleScrollToMessage(this.errorMessages.length === 0);
		} else {
			const errorMessage = `${this.constructor.name} - The request failed with a status of: ${response.status}`;

			payload.messages.errors.push = errorMessage;
		}

		this.submitButtonElement.disabled = false;
		this.submitButtonElement[this.submitButtonTextProperty] = this.submitButtonText;

		this.successMessages = [];
		this.errorMessages = [];

		// Dispatch global addToCart event
		EventBus.dispatchEvent('MVPS:multiAddToCart', payload);
	}

	/**
	 * Handle scroll to message functionality.
	 *
	 * @param  {Boolean} isSuccess Flag to specify if the add to cart response was successful.
	 * @return {Undefined|void}    Returns undefined if the scroll to message functionality should not run.
	 */
	handleScrollToMessage (isSuccess) {
		if (this.scrollToMessage.mode === false) {
			return;
		}

		this.#validator.isBoolean('handleScrollToMessage.isSuccess', isSuccess);

		let scrollToMessage = this.scrollToMessage.mode === true;

		// eslint-disable-next-line no-extra-parens
		if (!scrollToMessage && ((isSuccess && this.scrollToMessage.mode === 'success') || (!isSuccess && this.scrollToMessage.mode === 'error'))) {
			scrollToMessage = true;
		}

		if (!scrollToMessage) {
			return;
		}

		this.messageContainer.container.scrollIntoView(this.scrollToMessage.scrollIntoView);
	}

	/**
	 * Handle successful add to cart response.
	 */
	onSuccess () {
		this.successMessages.push(this.successMessage.message);
	}

	/**
	 * Handle "other" add to cart response. This is a catch all method for response handling.
	 */
	onOther () {
		this.errorMessages.push(this.otherMessage.message);
	}

	/**
	 * Get the error message element from the response payload data.
	 *
	 * @param  {Document} payloadData The Document object returned by the response payload data.
	 * @return {DOMString|String}     The error message element's inner HTML or an empty string if the error message element does not exist.
	 */
	getErrorMessage (payloadData) {
		const errorMessageElement = payloadData.querySelector(this.errorMessageSelector);

		if (errorMessageElement) {
			return errorMessageElement.innerHTML;
		}

		return '';
	}

};
